 
 
import {
    $applyNodeReplacement,
    type DOMConversionMap,
    type DOMConversionOutput,
    type DOMExportOutput,
    type EditorConfig,
    type LexicalNode,
    type NodeKey,
    TextNode,
} from 'lexical';

import { SerializedMentionNode } from './types';

// Helper Functions
const $convertMentionElement = ({ textContent, getAttribute }: HTMLElement): DOMConversionOutput | null => {
    const mentionName = getAttribute('data-lexical-mention-name');

    if (textContent !== null) {
        const node = $createMentionNode(
            typeof mentionName === 'string' ? mentionName : textContent,
            textContent,
        );
        return { node };
    }

    return null;
};

// Node Class
export class MentionNode extends TextNode {
    __mention: string;

    // Static Methods
    static getType(): string {
        return 'mention';
    }

    static clone(node: MentionNode): MentionNode {
        return new MentionNode(node.__mention, node.__text, node.__key);
    }

    static importJSON(serializedNode: SerializedMentionNode): MentionNode {
        return $createMentionNode(serializedNode.mentionName).updateFromJSON(serializedNode);
    }

    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-lexical-mention')) {
                    return null;
                }
                return {
                    conversion: $convertMentionElement,
                    priority: 1,
                };
            },
        };
    }

    // Constructor
    constructor(mentionName: string, text?: string, key?: NodeKey) {
        super(text ?? mentionName, key);
        this.__mention = mentionName;
    }

    // DOM Methods
    createDOM(config: EditorConfig): HTMLElement {
        const dom = super.createDOM(config);
        dom.className = 'NSRichTextEditor__mention';
        dom.spellcheck = false;
        return dom;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('span');
        element.setAttribute('data-lexical-mention', 'true');
        if (this.__text !== this.__mention) {
            element.setAttribute('data-lexical-mention-name', this.__mention);
        }
        element.textContent = this.__text;
        element.className = 'NSRichTextEditor__mention';
        return { element };
    }

    // Node Behavior Methods
    isTextEntity(): true {
        return true;
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    canInsertTextAfter(): boolean {
        return false;
    }

    // Serialization Methods
    exportJSON(): SerializedMentionNode {
        return {
            ...super.exportJSON(),
            mentionName: this.__mention,
        };
    }
}

// Utility Functions
export function $createMentionNode(
    mentionName: string,
    textContent?: string,
): MentionNode {
    const mentionNode = new MentionNode(mentionName, textContent ?? mentionName);
    mentionNode.setMode('segmented').toggleDirectionless();
    return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(
    node: LexicalNode | null | undefined,
): node is MentionNode {
    return node instanceof MentionNode;
}
